import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const STATUS_PRESET: Record<
  string,
  { label: string; dot: string; pill: string }
> = {
  // Agent statuses
  running: {
    label: "Active",
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700 ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/30",
  },
  creating: {
    label: "Setting up",
    dot: "bg-amber-500 animate-pulse",
    pill: "bg-amber-50 text-amber-800 ring-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30",
  },
  restarting: {
    label: "Restarting",
    dot: "bg-amber-500 animate-pulse",
    pill: "bg-amber-50 text-amber-800 ring-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30",
  },
  // The brain doesn't answer while the Boo is running (lib/brain-state.ts).
  "brain-starting": {
    label: "Starting",
    dot: "bg-blue-500",
    pill: "bg-blue-50 text-blue-800 ring-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/30",
  },
  "brain-offline": {
    label: "Offline",
    dot: "bg-orange-600",
    pill: "bg-orange-50 text-orange-800 ring-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/30",
  },
  idle: {
    label: "Idle",
    dot: "bg-sky-500",
    pill: "bg-sky-50 text-sky-700 ring-sky-500/30 dark:bg-sky-500/10 dark:text-sky-400 dark:ring-sky-500/30",
  },
  stopped: {
    label: "Paused",
    dot: "bg-muted-foreground/50",
    pill: "bg-muted text-muted-foreground ring-border",
  },
  error: {
    label: "Failed",
    dot: "bg-destructive",
    pill: "bg-destructive/10 text-destructive ring-destructive/30 dark:bg-destructive/15",
  },
  deleted: {
    label: "Deleted",
    dot: "bg-muted-foreground/50",
    pill: "bg-muted text-muted-foreground ring-border",
  },
  // Invoice statuses
  paid: {
    label: "Paid",
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700 ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/30",
  },
  // Broker integration account statuses
  connected: {
    label: "Connected",
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700 ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/30",
  },
  expired: {
    label: "Expired",
    dot: "bg-amber-500",
    pill: "bg-amber-50 text-amber-800 ring-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30",
  },
  issued: {
    label: "Issued",
    dot: "bg-blue-500",
    pill: "bg-blue-50 text-blue-700 ring-blue-500/30 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/30",
  },
  overdue: {
    label: "Overdue",
    dot: "bg-destructive",
    pill: "bg-destructive/10 text-destructive ring-destructive/30 dark:bg-destructive/15",
  },
  draft: {
    label: "Draft",
    dot: "bg-muted-foreground/50",
    pill: "bg-muted text-muted-foreground ring-border",
  },
  void: {
    label: "Void",
    dot: "bg-muted-foreground/30",
    pill: "bg-muted text-muted-foreground ring-border",
  },
  // Member statuses
  active: {
    label: "Active",
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700 ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/30",
  },
  invited: {
    label: "Invited",
    dot: "bg-amber-500",
    pill: "bg-amber-50 text-amber-800 ring-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30",
  },
  email_failed: {
    label: "Email failed",
    dot: "bg-destructive",
    pill: "bg-destructive/10 text-destructive ring-destructive/30 dark:bg-destructive/15",
  },
};

export function StatusBadge({
  status,
  errorMessage,
}: {
  status: string;
  errorMessage?: string | null;
}) {
  const preset = STATUS_PRESET[status] ?? {
    label: titleCase(status),
    dot: "bg-muted-foreground/50",
    pill: "bg-muted text-muted-foreground ring-border",
  };

  const badge = (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${preset.pill}`}
    >
      <span className={`size-1.5 rounded-full ${preset.dot}`} />
      {preset.label}
    </span>
  );

  if (errorMessage) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{badge}</span>
          </TooltipTrigger>
          <TooltipContent>{errorMessage}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return badge;
}

function titleCase(s: string): string {
  return s.replace(/(^|[\s_-])(\w)/g, (_, sep, ch) => sep + ch.toUpperCase());
}
